import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { formatNumberRu } from '../lib/format-number.js';
import { Button } from './button.js';
import { Input } from './input.js';
import { Select } from './select.js';
/** Размеры страницы по умолчанию (спека «Контент» v2 §1). */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;
/** Число страниц для `total` строк; 0 строк — 0 страниц. */
export function pageCountOf(total, pageSize) {
    if (!(total > 0) || !(pageSize > 0))
        return 0;
    return Math.ceil(total / pageSize);
}
/** Номер страницы, зажатый в `1…pageCount` (при 0 страниц — 1). */
export function clampPage(page, pageCount) {
    if (!Number.isFinite(page))
        return 1;
    return Math.min(Math.max(1, Math.trunc(page)), Math.max(1, pageCount));
}
/**
 * Окно номеров страниц: первая, последняя, текущая и по `siblings` соседей,
 * пропуски — многоточием. Многоточие никогда не заменяет ОДНУ страницу: там,
 * где пропущен один номер, показывается сам номер (`1 2 3 4 5 … 20`, а не
 * `1 … 3 4 5 … 20`) — кнопка той же ширины, что и «…», но ведёт куда-то.
 *
 * `pageCount` 0 → `[]`, 1 → `[1]`.
 */
export function pageWindow(page, pageCount, siblings = 1) {
    if (pageCount <= 0)
        return [];
    const current = clampPage(page, pageCount);
    // Сколько кнопок помещается без многоточий: первая + последняя + текущая
    // + соседи + два места под многоточия.
    const slots = siblings * 2 + 5;
    if (pageCount <= slots)
        return Array.from({ length: pageCount }, (_, i) => i + 1);
    const left = Math.max(current - siblings, 1);
    const right = Math.min(current + siblings, pageCount);
    const showStart = left > 3;
    const showEnd = right < pageCount - 2;
    if (!showStart) {
        const count = 3 + siblings * 2;
        return [...Array.from({ length: count }, (_, i) => i + 1), 'ellipsis-end', pageCount];
    }
    if (!showEnd) {
        const count = 3 + siblings * 2;
        return [
            1,
            'ellipsis-start',
            ...Array.from({ length: count }, (_, i) => pageCount - count + 1 + i),
        ];
    }
    return [
        1,
        'ellipsis-start',
        ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
        'ellipsis-end',
        pageCount,
    ];
}
/** «Показано 51–100 из 1 234»; пустой список — «Ничего не найдено». */
export function paginationSummaryRu(page, pageSize, total) {
    if (total === 0)
        return 'Ничего не найдено';
    const from = (page - 1) * pageSize + 1;
    if (total === null) {
        return `Показано ${formatNumberRu(from)}–${formatNumberRu(page * pageSize)}`;
    }
    const to = Math.min(page * pageSize, total);
    if (from > total)
        return `Показано 0 из ${formatNumberRu(total)}`;
    return `Показано ${formatNumberRu(from)}–${formatNumberRu(to)} из ${formatNumberRu(total)}`;
}
/**
 * Постраничная навигация таблицы: «Показано X–Y из N» · ‹ 1 … 4 [5] 6 … 25 › ·
 * «По 50» · «Перейти к __».
 *
 * Чисто презентационная: страница и размер живут у вызывающего (в URL),
 * компонент только сообщает намерение. Всё управляется с клавиатуры —
 * кнопки и поля в порядке табуляции, переход по Enter, у текущей страницы
 * `aria-current="page"`.
 */
export function Pagination({ page, pageSize, total, pageSizeOptions = PAGE_SIZE_OPTIONS, onPageChange, onPageSizeChange, isFetching = false, hasNextPage = false, siblings = 1, id, className, }) {
    const autoId = React.useId();
    const baseId = id ?? `pagination-${autoId}`;
    const pageCount = total === null ? null : pageCountOf(total, pageSize);
    const current = pageCount === null ? Math.max(1, page) : clampPage(page, pageCount);
    const canPrev = current > 1;
    const canNext = pageCount === null ? hasNextPage : current < pageCount;
    const go = (target) => {
        const next = pageCount === null ? Math.max(1, target) : clampPage(target, pageCount);
        if (next !== current)
            onPageChange(next);
    };
    const sizeOptions = pageSizeOptions.includes(pageSize)
        ? pageSizeOptions
        : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
    return (_jsxs("div", { className: cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-[var(--color-text-secondary)]', className), children: [_jsx("span", { "aria-live": "polite", className: "tabular-nums", children: paginationSummaryRu(current, pageSize, total) }), _jsxs("nav", { "aria-label": "\u0421\u0442\u0440\u0430\u043D\u0438\u0446\u044B", className: "flex items-center gap-1", children: [_jsx(Button, { type: "button", variant: "ghost", size: "icon", "aria-label": "\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0430\u044F \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0430", disabled: !canPrev || isFetching, onClick: () => go(current - 1), children: _jsx(ChevronLeft, { "aria-hidden": true, className: "h-4 w-4" }) }), pageCount !== null
                        ? pageWindow(current, pageCount, siblings).map((item) => typeof item === 'number' ? (_jsx(Button, { type: "button", size: "sm", variant: item === current ? 'primary' : 'ghost', className: "min-w-8 px-2 tabular-nums", "aria-label": `Страница ${item}`, "aria-current": item === current ? 'page' : undefined, disabled: isFetching && item !== current, onClick: () => go(item), children: item }, item)) : (_jsx("span", { "aria-hidden": true, className: "inline-flex h-8 min-w-8 items-center justify-center text-[var(--color-text-muted)]", children: "\u2026" }, item)))
                        : null, _jsx(Button, { type: "button", variant: "ghost", size: "icon", "aria-label": "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0430\u044F \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0430", disabled: !canNext || isFetching, onClick: () => go(current + 1), children: _jsx(ChevronRight, { "aria-hidden": true, className: "h-4 w-4" }) })] }), _jsxs("div", { className: "flex items-center gap-3", children: [onPageSizeChange ? (_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { id: `${baseId}-size-label`, children: "\u041F\u043E" }), _jsxs(Select, { value: String(pageSize), onValueChange: (v) => {
                                    const n = Number(v);
                                    if (Number.isFinite(n) && n !== pageSize)
                                        onPageSizeChange(n);
                                }, disabled: isFetching, children: [_jsx(Select.Trigger, { id: `${baseId}-size`, size: "sm", className: "w-20", "aria-label": "\u0421\u0442\u0440\u043E\u043A \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435", children: _jsx(Select.Value, {}) }), _jsx(Select.Content, { children: sizeOptions.map((n) => (_jsx(Select.Item, { value: String(n), children: n }, n))) })] })] })) : null, pageCount !== null && pageCount > 1 ? (_jsx(PageJump, { id: `${baseId}-jump`, current: current, pageCount: pageCount, disabled: isFetching, onJump: go })) : null] })] }));
}
function PageJump({ id, current, pageCount, disabled, onJump, }) {
    const [draft, setDraft] = React.useState('');
    const commit = () => {
        const trimmed = draft.trim();
        setDraft('');
        if (trimmed === '')
            return;
        const n = Number(trimmed);
        if (!Number.isFinite(n))
            return;
        onJump(clampPage(n, pageCount));
    };
    return (_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { children: "\u041F\u0435\u0440\u0435\u0439\u0442\u0438 \u043A" }), _jsx(Input, { id: id, type: "number", inputMode: "numeric", min: 1, max: pageCount, "aria-label": `Перейти к странице (1–${pageCount})`, placeholder: String(current), className: "h-8 w-20 tabular-nums", value: draft, disabled: disabled, onChange: (e) => setDraft(e.target.value), onBlur: commit, onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commit();
                    }
                    else if (e.key === 'Escape') {
                        setDraft('');
                    }
                } })] }));
}
//# sourceMappingURL=pagination.js.map